/**
 * Hooks TanStack Query — Boutique TEXTIPRINT (Sprint 1).
 * Brief SIM-PLATFORM-BOUTIQUE-2026-001.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  boutiqueFamillesApi, boutiqueSousCategoriesApi, boutiqueProduitsApi, boutiqueVariantesApi,
  boutiqueStocksApi, boutiqueMouvementsApi, boutiqueRemisesVolumeApi,
  boutiqueClientsApi, boutiqueAdressesClientApi, boutiqueFichiersClientApi,
  type FamilleListParams, type CreateFamillePayload, type UpdateFamillePayload,
  type SousCategorieListParams, type CreateSousCategoriePayload, type UpdateSousCategoriePayload,
  type ProduitListParams, type CreateProduitPayload, type UpdateProduitPayload,
  type VarianteListParams, type CreateVariantePayload, type UpdateVariantePayload,
  type StockListParams, type CreateStockPayload, type UpdateStockPayload, type AjusterStockPayload,
  type MouvementListParams,
  type RemiseVolumeListParams, type CreateRemiseVolumePayload, type UpdateRemiseVolumePayload,
  type ClientListParams, type CreateClientPayload, type UpdateClientPayload,
  type AdresseClientListParams, type CreateAdresseClientPayload, type UpdateAdresseClientPayload,
  type FichierClientListParams, type CreateFichierClientPayload,
} from '@/api/textprint-boutique';

const K = {
  familles:        ['boutique', 'familles']         as const,
  sousCategories:  ['boutique', 'sous-categories']  as const,
  produits:        ['boutique', 'produits']         as const,
  variantes:       ['boutique', 'variantes']        as const,
  stocks:          ['boutique', 'stocks']           as const,
  mouvements:      ['boutique', 'mouvements']       as const,
  remisesVolume:   ['boutique', 'remises-volume']   as const,
  clients:         ['boutique', 'clients']          as const,
  adressesClient:  ['boutique', 'adresses-client']  as const,
  fichiersClient:  ['boutique', 'fichiers-client']  as const,
};

// ── Familles ──────────────────────────────────────────────────────────────
export const useFamillesBoutique = (params: FamilleListParams = {}) =>
  useQuery({ queryKey: [...K.familles, params], queryFn: () => boutiqueFamillesApi.list(params), staleTime: 60_000 });

export const useFamilleBoutique = (id: string | undefined) =>
  useQuery({ queryKey: [...K.familles, id], queryFn: () => boutiqueFamillesApi.get(id!), enabled: !!id, staleTime: 30_000 });

export const useCreateFamilleBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateFamillePayload) => boutiqueFamillesApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.familles }),
  });
};

export const useUpdateFamilleBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFamillePayload }) => boutiqueFamillesApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.familles }),
  });
};

export const useDeleteFamilleBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boutiqueFamillesApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.familles }),
  });
};

// ── Sous-catégories ───────────────────────────────────────────────────────
export const useSousCategoriesBoutique = (params: SousCategorieListParams = {}) =>
  useQuery({ queryKey: [...K.sousCategories, params], queryFn: () => boutiqueSousCategoriesApi.list(params), staleTime: 60_000 });

export const useCreateSousCategorieBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateSousCategoriePayload) => boutiqueSousCategoriesApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.sousCategories }),
  });
};

export const useUpdateSousCategorieBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSousCategoriePayload }) => boutiqueSousCategoriesApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.sousCategories }),
  });
};

export const useDeleteSousCategorieBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boutiqueSousCategoriesApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.sousCategories }),
  });
};

// ── Produits ──────────────────────────────────────────────────────────────
export const useProduitsBoutique = (params: ProduitListParams = {}) =>
  useQuery({ queryKey: [...K.produits, params], queryFn: () => boutiqueProduitsApi.list(params), staleTime: 30_000 });

export const useProduitBoutique = (id: string | undefined) =>
  useQuery({ queryKey: [...K.produits, id], queryFn: () => boutiqueProduitsApi.get(id!), enabled: !!id, staleTime: 15_000 });

export const useCreateProduitBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateProduitPayload) => boutiqueProduitsApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.produits }),
  });
};

export const useUpdateProduitBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateProduitPayload }) => boutiqueProduitsApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.produits }),
  });
};

export const useDeleteProduitBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boutiqueProduitsApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.produits }),
  });
};

// ── Variantes ─────────────────────────────────────────────────────────────
export const useVariantesBoutique = (params: VarianteListParams = {}) =>
  useQuery({ queryKey: [...K.variantes, params], queryFn: () => boutiqueVariantesApi.list(params), staleTime: 60_000 });

export const useCreateVarianteBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateVariantePayload) => boutiqueVariantesApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.variantes }),
  });
};

export const useUpdateVarianteBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateVariantePayload }) => boutiqueVariantesApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.variantes }),
  });
};

export const useDeleteVarianteBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boutiqueVariantesApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.variantes }),
  });
};

// ── Stocks ────────────────────────────────────────────────────────────────
export const useStocksBoutique = (params: StockListParams = {}) =>
  useQuery({ queryKey: [...K.stocks, params], queryFn: () => boutiqueStocksApi.list(params), staleTime: 15_000 });

export const useCreateStockBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateStockPayload) => boutiqueStocksApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.stocks }),
  });
};

export const useUpdateStockBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateStockPayload }) => boutiqueStocksApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.stocks }),
  });
};

export const useAjusterStockBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AjusterStockPayload }) => boutiqueStocksApi.ajusterStock(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: K.stocks });
      qc.invalidateQueries({ queryKey: K.mouvements });
    },
  });
};

// ── Mouvements (lecture seule, sauf DELETE admin) ─────────────────────────
export const useMouvementsBoutique = (params: MouvementListParams = {}) =>
  useQuery({ queryKey: [...K.mouvements, params], queryFn: () => boutiqueMouvementsApi.list(params), staleTime: 15_000 });

export const useDeleteMouvementBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boutiqueMouvementsApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.mouvements }),
  });
};

// ── Remises volume ────────────────────────────────────────────────────────
export const useRemisesVolumeBoutique = (params: RemiseVolumeListParams = {}) =>
  useQuery({ queryKey: [...K.remisesVolume, params], queryFn: () => boutiqueRemisesVolumeApi.list(params), staleTime: 60_000 });

export const useCreateRemiseVolumeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateRemiseVolumePayload) => boutiqueRemisesVolumeApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.remisesVolume }),
  });
};

export const useUpdateRemiseVolumeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRemiseVolumePayload }) => boutiqueRemisesVolumeApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.remisesVolume }),
  });
};

export const useDeleteRemiseVolumeBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boutiqueRemisesVolumeApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.remisesVolume }),
  });
};

// ── Clients ───────────────────────────────────────────────────────────────
export const useClientsBoutique = (params: ClientListParams = {}) =>
  useQuery({ queryKey: [...K.clients, params], queryFn: () => boutiqueClientsApi.list(params), staleTime: 30_000 });

export const useClientBoutique = (id: string | undefined) =>
  useQuery({ queryKey: [...K.clients, id], queryFn: () => boutiqueClientsApi.get(id!), enabled: !!id, staleTime: 15_000 });

export const useCreateClientBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateClientPayload) => boutiqueClientsApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.clients }),
  });
};

export const useUpdateClientBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateClientPayload }) => boutiqueClientsApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.clients }),
  });
};

export const useDeleteClientBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boutiqueClientsApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.clients }),
  });
};

// ── Adresses client ──────────────────────────────────────────────────────
export const useAdressesClientBoutique = (params: AdresseClientListParams = {}) =>
  useQuery({ queryKey: [...K.adressesClient, params], queryFn: () => boutiqueAdressesClientApi.list(params), staleTime: 60_000 });

export const useCreateAdresseClientBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateAdresseClientPayload) => boutiqueAdressesClientApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.adressesClient }),
  });
};

export const useUpdateAdresseClientBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAdresseClientPayload }) => boutiqueAdressesClientApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.adressesClient }),
  });
};

export const useDeleteAdresseClientBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boutiqueAdressesClientApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.adressesClient }),
  });
};

// ── Fichiers client (logos uploadés) ──────────────────────────────────────
export const useFichiersClientBoutique = (params: FichierClientListParams = {}) =>
  useQuery({ queryKey: [...K.fichiersClient, params], queryFn: () => boutiqueFichiersClientApi.list(params), staleTime: 60_000 });

export const useCreateFichierClientBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateFichierClientPayload) => boutiqueFichiersClientApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.fichiersClient }),
  });
};

export const useDeleteFichierClientBoutique = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boutiqueFichiersClientApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.fichiersClient }),
  });
};
